/**
 * FTC-Compliant rent and fee utilities.
 * Rule: If a fee is "Mandatory," it must be included in the total_monthly_rent calculation.
 */

/**
 * @param {Object} params
 * @param {number} params.baseRent - Base monthly rent
 * @param {Array<{name: string, amount: number, frequency?: string}>} [params.additionalMandatoryFees]
 * @returns {number} Total monthly cost (base + mandatory monthly fees)
 */
export function calculateTotalMonthlyCost({
  baseRent,
  additionalMandatoryFees,
}) {
  const base = Number(baseRent) || 0;
  if (
    !Array.isArray(additionalMandatoryFees) ||
    additionalMandatoryFees.length === 0
  ) {
    return base;
  }
  let feesTotal = 0;
  for (const fee of additionalMandatoryFees) {
    const amt = Number(fee.amount) || 0;
    const freq = fee.frequency || "monthly";
    if (freq === "monthly") {
      feesTotal += amt;
    } else if (freq === "weekly") {
      feesTotal += amt * 4.33;
    }
  }
  return Math.round((base + feesTotal) * 100) / 100;
}

/**
 * Build itemized fee string for FTC transparency.
 * @param {Object} params
 * @param {number} params.baseRent
 * @param {Array<{name: string, amount: number, frequency?: string}>} [params.additionalMandatoryFees]
 * @returns {string} e.g. "Rent: $2,000 + $25 Trash + $15 Tech Fee = $2,040 total"
 */
export function buildItemizedFeeDescription({
  baseRent,
  additionalMandatoryFees,
}) {
  const base = Number(baseRent) || 0;
  const total = calculateTotalMonthlyCost({
    baseRent: base,
    additionalMandatoryFees,
  });
  if (
    !Array.isArray(additionalMandatoryFees) ||
    additionalMandatoryFees.length === 0
  ) {
    return `Rent: $${base.toLocaleString()} = $${total.toLocaleString()} total`;
  }
  const monthlyFees = additionalMandatoryFees.filter(
    (f) =>
      (f.frequency || "monthly") === "monthly" && (Number(f.amount) || 0) > 0
  );
  const parts = [`Rent: $${base.toLocaleString()}`];
  for (const fee of monthlyFees) {
    parts.push(`$${Number(fee.amount).toLocaleString()} ${fee.name}`);
  }
  parts.push(`= $${total.toLocaleString()} total`);
  return parts.join(" + ");
}

/**
 * Check if listing/unit has fully itemized fee structure (has additional_mandatory_fees defined).
 * @param {Object} item - Unit or listing
 * @returns {boolean}
 */
export function hasItemizedFees(item) {
  return (
    Array.isArray(item?.additional_mandatory_fees) &&
    item.additional_mandatory_fees.length > 0
  );
}
